import type { QueryResult } from '@apollo/client'
import type {
  ComplementaryProductsQuery,
  ComplementaryProductsQueryVariables,
} from '@shared/gql/document-nodes'
import type { FC, ReactNode } from 'react'

import { useQuery } from '@apollo/client'
import { ComplementaryProductsDocument, ProductType } from '@shared/gql/document-nodes'
import { createContext, useContext, useMemo, useState } from 'react'
import { useGetClientCartItemsByType } from 'utils/cart/use-get-client-cart-items-by-type'
import { useStoreId } from 'utils/hooks/store/use-store-id'
import { useIsClientSide } from 'utils/hooks/use-is-client-side'

export interface ComplementaryProductsContext {
  complementaryProductsQuery: QueryResult<
    ComplementaryProductsQuery,
    ComplementaryProductsQueryVariables
  >
  impressionTracked: boolean
  markAsTracked: () => void
}

interface ComplementaryProductsContextProviderProps {
  children?: ReactNode
  focusOnEan?: string
  limit?: number
}

export const ComplementaryProductsContext = createContext<ComplementaryProductsContext | null>(null)

export const useComplementaryProductsContext = (): ComplementaryProductsContext => {
  const ctxValue = useContext(ComplementaryProductsContext)
  if (ctxValue === null) {
    throw new Error('Expected context value to be set')
  }
  return ctxValue
}

export const ComplementaryProductsContextProvider: FC<
  ComplementaryProductsContextProviderProps
> = ({ children, focusOnEan, limit = 12 }) => {
  const { storeId } = useStoreId()
  const cartItems = useGetClientCartItemsByType(ProductType.Product)
  const isClientSide = useIsClientSide()
  const cartEans = useMemo(
    () => cartItems.map((item) => item.ean),
    // taking snapshot of cart items once after client hydration, so complementary product list remains unchange when cart content changes for better UX
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isClientSide, focusOnEan, storeId],
  )
  const complementaryProductsQuery = useQuery<
    ComplementaryProductsQuery,
    ComplementaryProductsQueryVariables
  >(ComplementaryProductsDocument, {
    variables: {
      eans: cartEans,
      storeId,
      focusOnEan: focusOnEan ?? null,
      limit: limit,
    },
    skip: (!cartItems.length && !focusOnEan) || !storeId,
  })

  /** Use this to keep prevent duplicated tracking, if different html elements are rendered for different screen sizes */
  const [impressionTracked, setImpressionTracked] = useState(false)

  const markAsTracked = () => {
    setImpressionTracked(true)
  }

  return (
    <ComplementaryProductsContext.Provider
      value={{ complementaryProductsQuery, impressionTracked, markAsTracked }}
    >
      {children}
    </ComplementaryProductsContext.Provider>
  )
}
